/**
 * T-4 · Créer un exercice ciblé — construit jusqu'où le contrat le permet.
 *
 * La séquence conçue est en quatre étapes : **cibler → générer → vérifier →
 * ajouter** au catalogue de l'élève, marqué « origine tuteur ». L'étape de
 * vérification est une étape, jamais une option : le contrôle humain par
 * instance est obligatoire sur cette voie.
 *
 * CE QUI EXISTE ET EST BRANCHÉ ICI : l'étape 1, « cibler ». Elle est
 * pré-remplie depuis l'unité de la file du matin (`eleve`, `savoir`, `matiere`
 * en query) et complétée par les données réelles de l'élève — le palier actuel
 * du savoir visé et le palier suivant, lus dans `GET /v1/suivi/:id/progression`.
 * C'est là que se gagnent les quatre minutes : pas en accélérant les
 * formulaires, en n'en affichant aucun.
 *
 * CE QUI N'EXISTE PAS, ET QUI EST DÉCLARÉ PLUTÔT QUE SIMULÉ : les étapes 2 à 4.
 * `POST /v1/exercices/generer` est réservé au rôle `eleve`
 * (`requireRole(req.principal, ['eleve'])`, `content.routes.ts`) : un tuteur ne
 * peut pas l'appeler, et aucune route ne marque un exercice « origine tuteur »
 * ni ne l'ajoute au catalogue d'un élève. Afficher un générateur qui ne génère
 * rien serait pire que de dire qu'il n'existe pas : une fonction absente sans
 * explication se lit comme un bogue, une fonction simulée se lit comme une
 * donnée.
 */
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { LIBELLES_DIMENSION, NIVEAUX_MAITRISE, type Matiere } from '@silex/shared';

import { api } from '../api/client';
import { EmptyState } from '../components/EmptyState';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { PalierMaitrise } from '../components/PalierMaitrise';
import { matiereLabel } from '../lib/suivi-format';
import { libellePalier, matiereConsole, pluriel } from '../lib/tuteur-format';
import { useAsync } from '../lib/useAsync';
import { useTuteur } from '../tuteur/TuteurContext';

const ETAPES = ['Cibler', 'Générer', 'Vérifier', 'Ajouter'] as const;

/** Index de l'écran : d'où l'on part quand on n'arrive pas depuis une unité. */
export function ExercicesPage() {
  const { eleves } = useTuteur();
  return (
    <div className="content__inner">
      <div className="page-head">
        <div>
          <h1>Exercices ciblés</h1>
          <p className="page-sub">
            Trois exercices contre l'erreur observée, pas contre le savoir en général.
          </p>
        </div>
      </div>

      {eleves.status === 'loading' ? <Chargement quoi="votre périmètre" /> : null}
      {eleves.status === 'error' ? <ErreurRessource message={eleves.message} /> : null}
      {eleves.status === 'ready' && eleves.data.nbEleves === 0 ? (
        <EmptyState title="Aucun élève ne vous est affecté">
          Un exercice ciblé se crée pour un élève précis, sur un savoir précis.
        </EmptyState>
      ) : null}
      {eleves.status === 'ready' && eleves.data.nbEleves > 0 ? (
        <div className="list">
          {eleves.data.eleves.map((e) => (
            <Link
              key={e.eleveId}
              className="li li--link"
              to={`/exercices/nouveau?eleve=${e.eleveId}`}
            >
              <span className="li-main">
                <span className="li-title">{e.prenom}</span>
                <span className="li-sub">
                  {e.matieresActives.map(matiereLabel).join(', ') || 'Aucune matière active'} ·{' '}
                  {pluriel(e.paniers.aRevoir, 'savoir à revoir', 'savoirs à revoir')}
                </span>
              </span>
              <span className="btn btn--ghost btn--sm">Cibler un savoir</span>
            </Link>
          ))}
        </div>
      ) : null}

      <AvertissementGeneration />
    </div>
  );
}

export function ExerciceCiblePage() {
  const [params] = useSearchParams();
  const eleveId = params.get('eleve');
  const savoirId = params.get('savoir');
  const matiereParam = params.get('matiere') as Matiere | null;
  const { eleves } = useTuteur();

  const progression = useAsync(
    useMemo(() => (eleveId ? () => api.getProgression(eleveId) : null), [eleveId]),
  );

  const eleve =
    eleves.status === 'ready' ? eleves.data.eleves.find((e) => e.eleveId === eleveId) : undefined;

  // LOT 1.4 — `progression.savoirs` porte UNE LIGNE PAR (savoir, dimension) :
  // un `find` sur le seul `savoirId` rendait, pour un savoir visuel, la
  // première ligne de l'ordre de la base. L'écran montrait donc un palier au
  // hasard sous le titre « Palier actuel », sans dire lequel des deux.
  const mesures =
    progression.status === 'ready' && savoirId
      ? progression.data.savoirs.filter((s) => s.savoirId === savoirId)
      : [];
  const savoir = mesures[0];

  const matiere: Matiere =
    savoir?.matiere ?? matiereParam ?? eleve?.matieresActives[0] ?? 'francais';

  // Le palier VISÉ est le palier suivant, jamais plus haut : un exercice trop
  // dur ne mesure rien. Aucune arithmétique de score ici — un rang sur l'échelle.
  const palierVise =
    savoir && savoir.niveauMaitrise !== 'maitrise'
      ? NIVEAUX_MAITRISE[NIVEAUX_MAITRISE.indexOf(savoir.niveauMaitrise) + 1]
      : undefined;

  if (!eleveId) {
    return (
      <div className="content__inner">
        <EmptyState title="Aucun élève ciblé">
          Ouvrez cet écran depuis une unité de votre file du matin ou depuis la fiche d'un élève.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="content__inner">
      <div className="dash-topbar">
        <div>
          <p className="breadcrumb">
            <Link to="/ma-journee">Ma journée</Link> / <Link to="/exercices">Exercices</Link> /{' '}
            {eleve?.prenom ?? 'élève'}
          </p>
          <h1>
            Exercices pour {eleve?.prenom ?? 'cet élève'}
            {savoir ? ` — ${savoir.enonce.toLowerCase()}` : ''}
          </h1>
        </div>
        <div className="stack-sm topbar-droite">
          <div className="stepper" aria-label="Étape 1 sur 4">
            {ETAPES.map((etape, i) => (
              <span key={etape} className={i === 0 ? 'st is-current' : 'st'}>
                <span className="n">{i + 1}</span> {etape}
              </span>
            ))}
          </div>
          <span className="chip-time">Étape 1 sur 4 · cible 4 min</span>
        </div>
      </div>

      {progression.status === 'error' ? <ErreurRessource message={progression.message} /> : null}

      <div className="card card--calm">
        <span className="stage-label">
          Ce que vous ciblez — pré-rempli depuis votre file du matin
        </span>
        <p className="lede">
          {eleve?.prenom ?? 'Élève'} · {matiereConsole(matiere)}
          {savoir ? ' · ' : ''}
          {savoir ? (
            <b>
              {savoir.code}, {savoir.enonce}
            </b>
          ) : (
            <span> · aucun savoir précisé dans le lien d'arrivée</span>
          )}
        </p>
      </div>

      <div className="tuteur-grid">
        <div className="stack">
          <section className="card">
            <h3>Étape 2 · Générer — non construite côté serveur</h3>
            <p className="lede">
              Aucun exercice n'est produit ici : la voie tuteur n'existe pas encore côté serveur.
            </p>
            <p className="note-line">
              Route existante : <code>POST /v1/exercices/generer</code> —{' '}
              <code>requireRole(req.principal, [&apos;eleve&apos;])</code>. Ce qui manque au produit
              est une voie tuteur avec son contrôle humain par instance, pas un bouton de plus dans
              cet écran.
            </p>
          </section>

          <section className="card">
            <h3>Étapes 3 et 4 · Vérifier, ajouter</h3>
          </section>
        </div>

        <div className="stack">
          <section className="card">
            <h3>Ce que le ciblage sait déjà</h3>
            {progression.status === 'loading' ? <Chargement quoi="la progression" /> : null}
            {savoir ? (
              <>
                {mesures.map((mesure) => (
                  <div className="savoir-row" key={mesure.dimension}>
                    <div className="savoir-row__main">
                      <span className="savoir-row__matiere">
                        Palier actuel · {LIBELLES_DIMENSION[mesure.dimension].toLowerCase()}
                      </span>
                      <span className="savoir-row__enonce">{mesure.enonce}</span>
                    </div>
                    <div className="savoir-row__meta">
                      <PalierMaitrise niveau={mesure.niveauMaitrise} />
                    </div>
                  </div>
                ))}
                <p className="note-line">
                  Palier visé :{' '}
                  {palierVise ? (
                    <b>{libellePalier(palierVise).toLowerCase()}</b>
                  ) : (
                    <b>aucun — le savoir est déjà maîtrisé</b>
                  )}
                  . Pas plus haut : un exercice trop dur ne mesure rien.
                </p>
              </>
            ) : (
              <p className="note-line">
                Aucun savoir n'est ciblé. Ouvrez la fiche de l'élève pour en choisir un observé, ou
                revenez par l'unité « blocage » de votre file du matin, qui en porte un.
              </p>
            )}
            <p className="note-line">
              <Link to={`/eleves/${eleveId}`}>Ouvrir sa fiche</Link> pour voir les autres savoirs
              observés et leurs paliers.
            </p>
          </section>

          <AvertissementGeneration />
        </div>
      </div>
    </div>
  );
}

function AvertissementGeneration() {
  return (
    <div className="card card--calm">
      <h3>Le contrôle humain n'est pas contournable</h3>
      <p className="note-line">
        Chaque exercice devra être lu avant d'atteindre un élève, et signé « origine tuteur » de
        votre nom.
      </p>
    </div>
  );
}
